/**
 * Multi-factor authentication API service — the four Bearer-authenticated
 * endpoints behind the account screen's Security tab.
 *
 * Sibling to authService rather than sharing a mapper with it, for one
 * behavioural reason: **a 401 here means the access token is dead, so it clears
 * the session and redirects; a 401 on the login endpoints must not**, or the
 * login page redirects to itself. Body reading is shared (httpResponse.js);
 * status mapping is not.
 *
 * The 403 handling is the other thing that cannot be inherited from the house
 * idiom. userDetailService maps 403 to ForbiddenError, and UserDetail renders a
 * 404 from it — correct there, where 403 means a revoked permission. Here 403
 * means the user mistyped their password or their code, and the right response
 * is an inline message with the enrollment left standing. mfa_ctrl.go's own
 * comment says the server chose 403 over 401 precisely so a typo would not
 * "discard a half-finished enrollment on a typo"; reusing ForbiddenError would
 * throw that away from the other end.
 */

import { API_BASE_URL, getAuthHeaders } from './api';
import { raiseSessionExpired } from './sessionExpiry';
import { readBody, messageFrom, sendRequest, readSuccessBody } from './httpResponse';
import {
  resolveExpiryDeadline,
  toClientClockSeconds,
  ENROLLMENT_FALLBACK_WINDOW_MS,
} from './expiryWindow';
import { formatDate } from './adminService';

/** The password was rejected. Show it against the password field. */
export class InvalidCredentialsError extends Error {
  constructor(message = 'That password is incorrect.') {
    super(message);
    this.name = 'InvalidCredentialsError';
  }
}

/**
 * The code was rejected. Show it against the code field.
 *
 * Note this is genuinely ambiguous on enroll/complete: an expired session_info
 * and a mistyped code both land here, and the server cannot distinguish them
 * (translateIdentityError maps both to ErrInvalidCode). The caller resolves it
 * from the clock rather than from this error — see EnrollmentDialog.
 */
export class InvalidCodeError extends Error {
  constructor(message = 'That code is incorrect.') {
    super(message);
    this.name = 'InvalidCodeError';
  }
}

/** A factor already exists. Only reachable from enroll/start. */
export class AlreadyEnrolledError extends Error {
  constructor(message = 'Multi-factor authentication is already enabled on this account.') {
    super(message);
    this.name = 'AlreadyEnrolledError';
  }
}

export class TooManyAttemptsError extends Error {
  constructor(message = 'Too many attempts. Wait a few minutes and try again.') {
    super(message);
    this.name = 'TooManyAttemptsError';
  }
}

export { SessionExpiredError } from './sessionExpiry';

/**
 * Map a non-OK response onto this module's error vocabulary. Always throws.
 *
 * Dispatch is on **(status, code)**, never on code alone. `error` is not a
 * single namespace — `mfa_required` comes from the login challenge and
 * `MFA_CHALLENGE_INVALID` from /login/mfa, neither of which reaches here today.
 * Keying on the pair means that stays true if one ever does.
 *
 * The 409 and 429 branches accept a *missing* code as well as the expected one.
 * Every error body on these four endpoints is JSON as of api-contract's
 * conversion, so that is belt-and-braces rather than load-bearing — but it
 * costs nothing and it survives an endpoint that has not been converted.
 *
 * Anything unrecognised falls through to one generic branch, which is where
 * Echo's own 404/405/panic-500 bodies land: they carry a `message` but no
 * `error`, so messageFrom declines them and the caller's fallback shows
 * instead of the framework's "Not Found".
 */
const raise = async (response) => {
  if (response.status === 401) {
    // Clears the session, starts the redirect, and throws. Nothing below runs.
    raiseSessionExpired();
  }

  const body = await readBody(response);
  const { code } = body;

  switch (response.status) {
    case 403:
      if (code === 'INVALID_CREDENTIALS') {
        throw new InvalidCredentialsError(messageFrom(body, undefined));
      }
      if (code === 'INVALID_CODE') {
        throw new InvalidCodeError(messageFrom(body, undefined));
      }
      // An unrecognised 403 is still not a session problem. Never redirect.
      throw new Error(messageFrom(body, 'That request was rejected.'));
    case 409:
      if (code === 'ALREADY_ENROLLED' || !code) {
        throw new AlreadyEnrolledError(messageFrom(body, undefined));
      }
      break;
    case 429:
      if (code === 'TOO_MANY_ATTEMPTS' || !code) {
        throw new TooManyAttemptsError(messageFrom(body, undefined));
      }
      break;
    default:
      break;
  }

  throw new Error(messageFrom(body, `Something went wrong (HTTP ${response.status}).`));
};

/**
 * Current MFA status for whoever holds the access token.
 *
 * There is no user id in the path or the body: this endpoint acts on the
 * bearer, never on a user named by the caller. That is why the Security tab is
 * structurally incapable of touching anyone else's factors.
 */
export const getMfaStatus = async () => {
  const response = await sendRequest(`${API_BASE_URL}/mfa`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    await raise(response);
  }

  // readSuccessBody, not readBody: the readBody rule is about *error* bodies,
  // where the format is mixed and .json() throws on text/plain and on a 204. A
  // 200 from these endpoints is always a JSON object, so there is nothing to
  // sniff — but "always" describes the API, not whatever is standing in front
  // of it, so the parse failure still needs a sentence rather than a SyntaxError.
  return readSuccessBody(response);
};

/**
 * Begin enrollment. Returns the shared secret, the otpauth URI to render as a
 * QR code, the session_info to carry into completion, and `expiresAt` — the
 * deadline bounding the window, in the form described below.
 *
 * 403 is only ever INVALID_CREDENTIALS here — StartEnrollment cannot return
 * ErrInvalidCode — so there is deliberately no INVALID_CODE handling at this
 * call site.
 *
 * ## The window comes back as a finished deadline, not as the server sent it
 *
 * `expires_at` does not survive this function. In its place is `expiresAt`: an
 * epoch-**milliseconds** deadline measured against the *browser's* clock, which
 * is the only clock the countdown consuming it can read. Same instant, same
 * shape and same name authService hands the login challenge — the two MFA
 * countdowns are fed identically on purpose, and neither dialog does any
 * resolving of its own.
 *
 * Two steps get it there, and both have to happen here rather than at the
 * countdown.
 *
 * The disagreement between the two clocks can only be measured from the `Date`
 * header of *this* response, which exists nowhere else; by the time the value
 * has been returned the evidence needed to correct it is gone. Skipping it is
 * not a rounding error: a browser six minutes slow reads a five-minute window
 * as an eleven-minute one — inside the plausibility cap, so nothing downstream
 * would object — and keeps counting long after the session it is counting has
 * died.
 *
 * What is left is then turned into a relative window and re-anchored to the
 * client's clock by resolveExpiryDeadline, with ENROLLMENT_FALLBACK_WINDOW_MS
 * standing in when the timestamp is missing, already past, or still
 * implausible. That fallback is deliberately shorter than the server's window,
 * because the two ways of being wrong are not equally bad: a countdown that
 * runs long lets an expired session come back as "that code is incorrect",
 * which is precisely the misattribution the countdown exists to prevent.
 *
 * Resolving at the boundary is also what keeps every future reader honest —
 * which is why `expires_at` is dropped rather than passed along beside its
 * resolved twin. No raw server timestamp escapes into the app for someone to
 * compare against a browser clock by accident.
 *
 * When the `Date` header is missing or unparseable — some proxies strip it —
 * the timestamp goes through the skew step untouched and the plausibility cap
 * inside resolveExpiryDeadline is the only guard left, exactly as it was before
 * any of this existed.
 */
export const startEnrollment = async (password) => {
  const response = await sendRequest(`${API_BASE_URL}/mfa/enroll/start`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ password }),
  });
  // Read before the body is awaited: `Date` is the server's clock when it sent
  // the response, so it must be paired with the moment that response arrived,
  // not with the moment its body finished streaming.
  const receivedAt = Date.now();

  if (!response.ok) {
    await raise(response);
  }

  const { expires_at: expiresAtSeconds, ...started } = await readSuccessBody(response);

  return {
    ...started,
    expiresAt: resolveExpiryDeadline(
      toClientClockSeconds(expiresAtSeconds, response.headers?.get?.('Date'), receivedAt),
      ENROLLMENT_FALLBACK_WINDOW_MS,
      // The same instant the response arrived, for both the delta and the
      // anchor. Reading the clock again here would let the body-parsing gap
      // above land inside the window instead of before it.
      receivedAt,
    ),
  };
};

/**
 * Finish enrollment. Answers 204 with no body on success, so nothing is parsed.
 */
export const completeEnrollment = async ({ password, sessionInfo, code }) => {
  const response = await sendRequest(`${API_BASE_URL}/mfa/enroll/complete`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ password, session_info: sessionInfo, code }),
  });

  if (!response.ok) {
    await raise(response);
  }
};

/**
 * Remove the enrolled factor. Requires both the password and a current code —
 * an MFA-enrolled user cannot obtain an ID token from a password alone.
 *
 * The path takes a factor id, but the server clears every factor the provider
 * holds, and its own guards ensure at most one can ever exist
 * (mfa_service.go:60-75). The UI is a single on/off toggle for that reason,
 * not a per-factor Remove.
 *
 * Answers 204 with no body on success.
 */
export const unenrollFactor = async (factorId, { password, code }) => {
  const response = await sendRequest(`${API_BASE_URL}/mfa/${encodeURIComponent(factorId)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    body: JSON.stringify({ password, code }),
  });

  if (!response.ok) {
    await raise(response);
  }
};

/**
 * Format an enrolled_at for display.
 *
 * The wire value is Unix **seconds**; formatDate takes something `new Date()`
 * understands, and `new Date(1733960000)` is read as milliseconds — January
 * 1970. Converting here rather than at each call site is what keeps that from
 * being rediscovered.
 *
 * Top-level `enrolled_at` is a *int64 with no omitempty, so it arrives as null
 * when never enrolled; the per-factor one is omitempty and simply absent. Both
 * reach the same fallback.
 */
export const formatEnrolledAt = (unixSeconds) =>
  typeof unixSeconds === 'number' && unixSeconds > 0
    ? formatDate(new Date(unixSeconds * 1000).toISOString())
    : 'Unknown';
